import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useBrandsListQuery } from "entities/brands/queries/usePagesListByBrandQuery";
import { Brand } from "entities/brands/types";
import { useDuplicatePageModuleMutation } from "entities/pages/mutations/useDuplicatePageModuleMutation";
import { useMovePageModuleMutation } from "entities/pages/mutations/useMovePageModuleMutation";
import { usePagesListByBrandQuery } from "entities/pages/queries/usePagesListByBrandQuery";
import { PageModuleData, Page } from "entities/pages/types";
import { FC, useEffect, useState, useMemo } from "react";
import Select from "shared/components/Form/Select";
import Modal from "shared/components/Modal";
import { COLOR_BLACK, COLOR_GREY_12 } from "shared/theme/colors";
import { usePageModules } from "../../context";
import { MoveOrDuplicateType } from "../../types";
import { createAlertMessage } from "./helpers";

type Props = {
  open: boolean;
  onClose: () => void;
};

const MoveOrDuplicateModuleModal: FC<Props> = ({ open, onClose }) => {
  const {
    brandId,
    pageId,
    moveOrDuplicateModalState,
    moveOrDuplicateType,
    moveOrDuplicateModule,
    setAlertOpen,
    setAlertMessage,
    setAlertIsSuccess,
  } = usePageModules();

  const [selectedBrandId, setSelectedBrandId] = useState<string>();
  const [selectedPageId, setSelectedPageId] = useState<string>();

  const { data: brands = [] } = useBrandsListQuery();
  const { data: pages = [], isLoading: isLoadingPages } =
    usePagesListByBrandQuery(selectedBrandId ?? "");

  const { mutate: mutateMoveModule, isPending: isMovingModule } =
    useMovePageModuleMutation();
  const { mutate: mutateDuplicateModule, isPending: isDuplicatingModule } =
    useDuplicatePageModuleMutation();

  const isLoading =
    moveOrDuplicateType === MoveOrDuplicateType.Move
      ? isMovingModule
      : isDuplicatingModule;

  useEffect(() => {
    if (open) {
      setSelectedBrandId(undefined);
      setSelectedPageId(undefined);
    }
  }, [open]);

  // Auto-select page if brand has only one page
  useEffect(() => {
    if (selectedBrandId && pages.length === 1) {
      setSelectedPageId(pages[0].id);
    }
  }, [selectedBrandId, pages]);

  const handleOperationResult = (
    isSuccess: boolean,
    targetBrandId: string,
    targetPageId: string,
  ) => {
    setAlertMessage(
      createAlertMessage(
        isSuccess,
        moveOrDuplicateModule!,
        targetBrandId,
        targetPageId,
        moveOrDuplicateType,
        brands,
        pages,
      ),
    );
    setAlertIsSuccess(isSuccess);
    setAlertOpen(true);
    setTimeout(() => {
      moveOrDuplicateModalState.closeModal();
    }, 0);
  };

  const handleMoveOrDuplicateSubmit = ({
    moveOrDuplicateModule,
    targetBrandId,
    targetPageId,
  }: {
    moveOrDuplicateModule: PageModuleData;
    targetBrandId: string;
    targetPageId: string;
  }) => {
    if (moveOrDuplicateType === MoveOrDuplicateType.Move) {
      mutateMoveModule(
        {
          module: moveOrDuplicateModule,
          sourceBrandId: brandId,
          sourcePageId: pageId,
          targetBrandId,
          targetPageId,
        },
        {
          onSuccess: () => {
            handleOperationResult(true, targetBrandId, targetPageId);
          },
          onError: () => {
            handleOperationResult(false, targetBrandId, targetPageId);
          },
        },
      );
    } else {
      mutateDuplicateModule(
        {
          module: moveOrDuplicateModule,
          sourceBrandId: brandId,
          sourcePageId: pageId,
          targetBrandId,
          targetPageId,
        },
        {
          onSuccess: () => {
            handleOperationResult(true, targetBrandId, targetPageId);
          },
          onError: () => {
            handleOperationResult(false, targetBrandId, targetPageId);
          },
        },
      );
    }
  };

  const handleSubmit = () => {
    if (!moveOrDuplicateModule || !selectedBrandId || !selectedPageId) return;

    handleMoveOrDuplicateSubmit({
      moveOrDuplicateModule,
      targetBrandId: selectedBrandId,
      targetPageId: selectedPageId,
    });
  };

  const title = `${moveOrDuplicateType === MoveOrDuplicateType.Move ? "Move" : "Duplicate"} Module`;
  const subtitle =
    moveOrDuplicateType === MoveOrDuplicateType.Move
      ? "Select the new destination for this module"
      : "Choose where to duplicate this module";

  const buttonTitle =
    moveOrDuplicateType === MoveOrDuplicateType.Move ? "Move" : "Duplicate";

  const brandOptions = brands.map((brand: Brand) => ({
    value: brand.id,
    label: brand.name.charAt(0).toUpperCase() + brand.name.slice(1),
  }));

  const filteredPages = useMemo(() => {
    if (
      moveOrDuplicateType === MoveOrDuplicateType.Move &&
      selectedBrandId === brandId
    ) {
      return pages.filter((page) => page.id !== pageId);
    }
    return pages;
  }, [pages, selectedBrandId, brandId, pageId, moveOrDuplicateType]);

  const pageOptions = filteredPages.map((page: Page) => ({
    value: page.id,
    label: page.title,
  }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      primaryButtonTitle={buttonTitle}
      onPrimaryClick={handleSubmit}
      isSubmitting={isLoading}
      primaryColor="primary"
      secondaryButtonTitle="Cancel"
      onSecondaryClick={onClose}
      isDisabled={!selectedBrandId || !selectedPageId}
    >
      <Box display="flex" flexDirection="column" gap="1rem">
        <Typography color={COLOR_GREY_12} variant="bodyLRegular">
          {subtitle}
        </Typography>

        <Select
          label="Brand"
          labelColor={COLOR_BLACK}
          value={selectedBrandId}
          handleChange={(value) => {
            setSelectedBrandId(value);
            setSelectedPageId("");
          }}
          options={brandOptions}
          placeholder="Select brand"
        />

        <Select
          label="Page"
          labelColor={COLOR_BLACK}
          value={selectedPageId}
          handleChange={(value) => setSelectedPageId(value)}
          options={pageOptions}
          placeholder="Select page"
          disabled={
            !selectedBrandId || isLoadingPages || pageOptions.length === 0
          }
        />
      </Box>
    </Modal>
  );
};

export default MoveOrDuplicateModuleModal;
